import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { TeamMembersList } from "./team/TeamMembersList";
import { InviteTeamMemberForm } from "./team/InviteTeamMemberForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { AlertCircle, RefreshCcw, Mail } from "lucide-react";

interface TeamMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamMembersDialog({ open, onOpenChange }: TeamMembersDialogProps) {
  const [activeTab, setActiveTab] = useState("team-members");
  const [debugToken, setDebugToken] = useState("");
  const [debugResult, setDebugResult] = useState<any>(null);
  const [manualFixEmail, setManualFixEmail] = useState("");
  const [isFixing, setIsFixing] = useState(false);
  const [isEmailTesting, setIsEmailTesting] = useState(false);
  const [manualFixId, setManualFixId] = useState("");
  const [forceFixing, setForceFixing] = useState(false);
  
  const { 
    teamMembers, 
    isLoading, 
    inviteTeamMember, 
    removeTeamMember, 
    updateTeamMemberRole,
    resendInvitation,
    isInviting,
    isRemoving,
    isResending,
    refetchTeamMembers,
    testInvitationEmail
  } = useTeamMembers();

  const handleTestEmail = async () => {
    if (!testInvitationEmail) {
      toast.error("Test function not available");
      return;
    }

    setIsEmailTesting(true);
    try {
      const result = await testInvitationEmail();
      if (result.success) {
        toast.success("Test email sent successfully!");
      } else {
        toast.error(`Test email failed: ${result.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error("Error testing email:", error);
      toast.error(`Error testing email: ${error.message}`);
    } finally {
      setIsEmailTesting(false);
    }
  };

  const handleDebugInvitation = async () => {
    if (import.meta.env.DEV) {
      try {
        const lastPendingInvite = teamMembers?.find(m => m.status === 'pending');
        
        if (!lastPendingInvite) {
          toast.error("No pending invitations found to debug");
          return;
        }
        
        console.log("Pending invitation details:", {
          id: lastPendingInvite.id,
          email: lastPendingInvite.email,
          token: lastPendingInvite.invite_token,
          tokenLength: lastPendingInvite.invite_token?.length || 0,
          status: lastPendingInvite.status
        });
        
        const origin = window.location.origin;
        const acceptUrl = `${origin}/accept-invitation?token=${encodeURIComponent(lastPendingInvite.invite_token || '')}`;
        
        console.log("Direct invitation link:", acceptUrl);
        toast.success("Debug info logged to console");
        
        await navigator.clipboard.writeText(acceptUrl);
        toast.success("Invitation link copied to clipboard");
      } catch (error) {
        console.error("Error debugging invitation:", error);
        toast.error("Error debugging invitation");
      }
    }
  };

  const handleManualFix = async () => {
    if (!manualFixEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setIsFixing(true);
    
    try {
      const { data: authUsers, error: authError } = await supabase.rpc(
        'get_user_id_by_email',
        { user_email: manualFixEmail }
      );
      
      if (authError || !authUsers || authUsers.length === 0) {
        toast.error(`No user found with email: ${manualFixEmail}`);
        console.error("Error finding user:", authError);
        return;
      }
      
      const userId = authUsers[0];
      
      const { data: pendingInvite, error: inviteError } = await supabase
        .from('team_members')
        .select('*')
        .eq('email', manualFixEmail)
        .eq('status', 'pending')
        .maybeSingle();
        
      if (inviteError) {
        console.error("Error finding invitation:", inviteError);
        toast.error(`Error finding invitation: ${inviteError.message}`);
        return;
      }
      
      if (!pendingInvite) {
        toast.error(`No pending invitation found for email: ${manualFixEmail}`);
        return;
      }
      
      const { data: updateResult, error: updateError } = await supabase
        .from('team_members')
        .update({
          user_id: userId,
          status: 'active',
          invite_token: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', pendingInvite.id)
        .select();
        
      if (updateError) {
        console.error("Error updating invitation:", updateError);
        toast.error(`Error updating invitation: ${updateError.message}`);
        return;
      }
      
      console.log("Invitation manually fixed:", updateResult);
      toast.success(`Invitation for ${manualFixEmail} has been manually activated`);
      
      refetchTeamMembers();
      setManualFixEmail("");
      
    } catch (error: any) {
      console.error("Error in manual fix:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsFixing(false);
    }
  };

  const handleForceAccept = async () => {
    if (!manualFixId) {
      toast.error("Please enter an invitation ID");
      return;
    }
    
    setForceFixing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to perform this action");
        return;
      }

      const { data: invitation, error: inviteError } = await supabase
        .from('team_members')
        .select('*')
        .eq('id', manualFixId)
        .maybeSingle();
        
      if (inviteError) {
        console.error("Error finding invitation:", inviteError);
        toast.error(`Error finding invitation: ${inviteError.message}`);
        return;
      }
      
      if (!invitation) {
        toast.error(`No invitation found with ID: ${manualFixId}`);
        return;
      }
      
      const { data: updateResult, error: updateError } = await supabase
        .from('team_members')
        .update({
          user_id: user.id,
          status: 'active',
          invite_token: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', manualFixId)
        .select();
        
      if (updateError) {
        console.error("Error updating invitation:", updateError);
        toast.error(`Error updating invitation: ${updateError.message}`);
        return;
      }
      
      console.log("Invitation manually activated:", updateResult);
      toast.success(`Invitation has been manually activated`);
      
      refetchTeamMembers();
      setManualFixId("");
      
    } catch (error: any) {
      console.error("Error in force accept:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setForceFixing(false);
    }
  };

  const handleTokenLookup = async () => {
    if (!debugToken || debugToken.length < 5) {
      toast.error("Please enter a valid token to check");
      return;
    }

    try {
      setDebugResult({ status: "checking" });
      
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("invite_token", debugToken);
        
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        setDebugResult({
          status: "found",
          invitation: data[0],
          message: `Token found! Status: ${data[0].status}`
        });
        toast.success("Token found in database");
      } else {
        const decodedToken = decodeURIComponent(debugToken);
        const { data: dataAlt, error: errorAlt } = await supabase
          .from("team_members")
          .select("*")
          .eq("invite_token", decodedToken);
          
        if (errorAlt) {
          throw errorAlt;
        }
        
        if (dataAlt && dataAlt.length > 0) {
          setDebugResult({
            status: "found-decoded",
            invitation: dataAlt[0],
            message: `Token found (after decoding)! Status: ${dataAlt[0].status}`
          });
          toast.success("Token found after decoding");
        } else {
          setDebugResult({
            status: "not-found",
            message: "Token not found in database"
          });
          toast.error("Token not found in database");
        }
      }
    } catch (error: any) {
      console.error("Error looking up token:", error);
      setDebugResult({
        status: "error",
        error: error.message || "Unknown error",
        message: "Error checking token"
      });
      toast.error(`Error checking token: ${error.message}`);
    }
  };

  const handleUpdateRole = (memberId: string, role: 'admin' | 'member') => {
    updateTeamMemberRole.mutate({ memberId, role });
  };

  const handleCheckEmailConfig = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-email-config', {
        body: { 
          check: true 
        }
      });
      
      console.log("Email config check result:", { data, error });
      
      if (error) {
        toast.error(`Email configuration check failed: ${error.message}`);
        return;
      }
      
      if (data?.success) {
        toast.success("Email configuration is valid");
        setDebugResult({
          status: "email-config-valid",
          message: "Email configuration is valid",
          details: data
        });
      } else {
        toast.error(`Email configuration issue: ${data?.message || 'Unknown issue'}`);
        setDebugResult({
          status: "email-config-invalid",
          message: "Email configuration issues found",
          details: data
        });
      }
    } catch (error: any) {
      console.error("Error checking email config:", error);
      toast.error(`Error checking email configuration: ${error.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Team Members</DialogTitle>
          <DialogDescription>
            Manage your team members and invite new ones.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="team-members">Team Members</TabsTrigger>
            <TabsTrigger value="invite">Invite</TabsTrigger>
          </TabsList>
          
          <TabsContent value="team-members" className="pt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium">Current Team Members</h3>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={refetchTeamMembers}
                disabled={isLoading}
              >
                <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                Refresh
              </Button>
            </div>
            
            <TeamMembersList
              teamMembers={teamMembers || []}
              isLoading={isLoading}
              onRemove={(memberId) => removeTeamMember.mutate(memberId)}
              onUpdateRole={handleUpdateRole}
              onResendInvitation={resendInvitation}
              isRemoving={isRemoving}
              isResending={isResending}
            />
            
            {import.meta.env.DEV && (
              <div className="mt-6 pt-4 border-t space-y-4">
                <h4 className="text-sm font-medium flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  Admin Troubleshooting Tools
                </h4>
                
                <div className="mt-2 space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDebugInvitation}
                      className="text-xs"
                    >
                      Debug Last Invitation & Copy Link
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleTestEmail}
                      disabled={isEmailTesting}
                      className="text-xs"
                    >
                      <Mail className="h-3.5 w-3.5 mr-1" />
                      {isEmailTesting ? 'Testing...' : 'Test Email Service'}
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCheckEmailConfig}
                      className="text-xs"
                    >
                      Check Email Config
                    </Button>
                  </div>
                
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Manual activation for pending invitations:</p>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="User email address" 
                        value={manualFixEmail} 
                        onChange={(e) => setManualFixEmail(e.target.value)}
                        className="text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleManualFix}
                        disabled={isFixing || !manualFixEmail}
                        className="text-xs whitespace-nowrap"
                      >
                        {isFixing ? 'Fixing...' : 'Manually Activate'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Force accept invitation by ID:</p>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Invitation ID" 
                        value={manualFixId} 
                        onChange={(e) => setManualFixId(e.target.value)}
                        className="text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleForceAccept}
                        disabled={forceFixing || !manualFixId}
                        className="text-xs whitespace-nowrap"
                      >
                        {forceFixing ? 'Fixing...' : 'Force Accept'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    <p className="text-xs text-gray-500">Check invitation token:</p>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Paste token here" 
                        value={debugToken} 
                        onChange={(e) => setDebugToken(e.target.value)}
                        className="text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleTokenLookup}
                        className="text-xs whitespace-nowrap"
                      >
                        Check Token
                      </Button>
                    </div>
                    
                    {debugResult && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs overflow-auto max-h-[200px]">
                        <p>{debugResult.message}</p>
                        <pre className="mt-2 text-[10px]">{JSON.stringify(debugResult, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="invite" className="pt-4">
            <InviteTeamMemberForm
              onInvite={inviteTeamMember}
              isInviting={isInviting}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
